<script setup lang="ts">
  import { ref, watch, type PropType } from 'vue';
  import { useViewQuery, useHttpClient, useRoute, useTranslator } from '@othercommerce/foundation';
  import type { ProductVariantResource } from '@storefront/resources';

  const props = defineProps({
    variant: { type: Object as PropType<ProductVariantResource>, required: true },
    reviews: { type: Object, required: true },
    reviewAvailable: { type: Boolean, required: true },
    reviewsSummary: { type: Object, required: true },
  });

  const route = useRoute();
  const translator = useTranslator();

  const sorting = [
    { label: translator.trans('SHOP.PRODUCT.LABEL.REVIEW_SORT_BY_DATE_ASC'), value: 'date_asc' },
    { label: translator.trans('SHOP.PRODUCT.LABEL.REVIEW_SORT_BY_DATE_DESC'), value: 'date_desc' },
    { label: translator.trans('SHOP.PRODUCT.LABEL.REVIEW_SORT_BY_RATING_ASC'), value: 'rating_asc' },
    { label: translator.trans('SHOP.PRODUCT.LABEL.REVIEW_SORT_BY_RATING_DESC'), value: 'rating_desc' },
  ];

  const http = useHttpClient();
  const query = useViewQuery();
  const reviewsToShow = ref(props.reviews);
  // @TODO hacky fix, fix later, simillar case to ShopProductsGridControll
  const sortValue = query.value?.sort;
  const sort = ref(typeof sortValue === 'function' ? null : sortValue);
  const open = ref(false);

  watch(() => props.reviews, () => {
    onChangeSort();
  });

  function onChangeSort() {
    http.get(route('api.v1.web.product.review.index', { product: props.variant.productId, sort: sort.value })).then((res) => {
      reviewsToShow.value = res.reviews;
    });
  }

  function onRedirect(page: number) {
    http.get(route('api.v1.web.product.review.index', { product: props.variant.productId, page })).then((res) => {
      reviewsToShow.value = res.reviews;
    });
  }
</script>
<template>
  <div class="product-reviews">
    <div class="product-reviews__reviews">
      <div class="product-reviews__reviews-header">
        <div class="product-reviews__stars">
          <span class="product-reviews__general-rating-value">{{ variant.reviews.average }} / 5</span>
          <SfStars :count="variant.reviews.count" :rating="variant.reviews.average" />
        </div>
        <ul class="product-reviews__opinions-counter">
          <li v-for="(review, key) in reviewsSummary" class="product-reviews__counter-item">
            <span class="product-reviews__counter-item-star">
              {{ key }}
              <SfIconStarEmpty aria-hidden="true" />
            </span>
            <div class="product-reviews__counter-progress">
              <div :style="{ 'width': review.percent + '%' }" class="product-reviews__counter-progress-value"></div>
            </div>
            <span class="product-reviews__counter-count">({{ review.count }})</span>
          </li>
        </ul>
      </div>
      <div class="product-reviews__rate" v-if="reviewAvailable">
        <span class="product-reviews__rate-hading">
          {{ $t('SHOP.PRODUCT.HEADER.REVIEW_DO_YOU_HAVE_PRODUCT') }}
        </span>
        <SfButton @click="open = true">
          <template #default>
            {{ $t('SHOP.PRODUCT.ACTION.REVIEW_ADD') }}
          </template>
          <template #after>
            <SfIconStarEmpty aria-hidden="true" />
          </template>
        </SfButton>
      </div>
      <p v-else class="product-reviews__info">
        {{ $t('SHOP.PRODUCT.MESSAGE.AUTHENTICATED_USER_REVIEW_NOTICE') }}
      </p>
    </div>
    <div class="product-reviews__opinions-wrapper" v-if="reviews.data.length > 0">
      <div class="product-reviews__sorting-wrapper">
        <SfFormSelect v-model="sort" :options="sorting" :label="$t('SHOP.COMMON.LABEL.SORT_TYPE')" :placeholder="$t('SHOP.COMMON.LABEL.SORT_DEFAULT')" @change="onChangeSort" class="product-reviews__sorting" />
      </div>
      <ShopProductViewDescriptionComment v-for="review in reviewsToShow.data" :review @refreshComments="onChangeSort" />
      <SfPagination :pagination="reviewsToShow" @redirect="onRedirect" />
    </div>
  </div>
  <ReviewFormModal :variant="variant" v-model:open="open" />
</template>
<style lang="scss">
  @use '@storefront/styles/mixins/media';

  :root {
    --shop-product-review-main-star-size: 2rem;
    --shop-product-review-main-count-font-size: 1rem;
    --shop-product-review-main-stars-gap: 0.5rem;
    --shop-product-review-general-rating-size: 1.75rem;

    --shop-product-review-gap: 2rem;
    --shop-product-review-comments-gap: 1rem;

    @include media.query(lg) {
      --shop-product-review-main-star-size: 1.25rem;
      --shop-product-review-main-count-font-size: 1rem;
      --shop-product-review-main-stars-gap: 0.125rem;
      --shop-product-review-general-rating-size: 1.25rem;
    }
  }

  .product-reviews {
    display: flex;
    flex-direction: column;
    gap: var(--shop-product-review-gap);

    &__empty-reviews {
      font-weight: var(--font-weight-semi);
    }

    &__reviews {
      display: flex;
      justify-content: space-between;
      gap: 4rem;
      align-items: center;
    }

    &__reviews-header {
      display: flex;
      gap: 4rem;
      flex-basis: 50%;
    }

    &__stars {
      --sf-stars-star-size: var(--shop-product-review-main-star-size);
      --sf-stars-font-size: var(--shop-product-review-main-count-font-size);
      --sf-stars-gap: var(--shop-product-review-main-stars-gap);
      --sf-stars-padding-y: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    &__general-rating-value {
      font-size: var(--shop-product-review-general-rating-size);
      font-weight: var(--font-weight-bold);
    }

    &__sorting-wrapper {
      display: flex;
      justify-content: flex-end;
      align-self: flex-end;

      .control__wrapper {
        width: auto;
      }
    }

    &__sorting {
      width: auto;
    }

    &__opinions-counter {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
      row-gap: 0.25rem;
    }

    &__counter-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      width: 100%;
    }

    &__counter-item-star {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.5rem;
      min-width: 2rem;
      font-size: 0.875rem;
      font-weight: var(--font-weight-semi);
    }

    &__counter-progress-value {
      height: 100%;
      background-color: var(--primary-color);
      border-radius: 0.5rem;
    }

    &__counter-progress {
      flex-grow: 1;
      height: 0.5rem;
      border-radius: 0.5rem;
      background-color: var(--disabled-background);
    }

    &__counter-count {
      min-width: 2rem;
      text-align: right;
      font-size: 0.875rem;
      color: var(--color-secondary);
    }

    &__rate-hading {
      font-weight: var(--font-weight-semi);
    }

    &__rate {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }

    &__opinions-wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--shop-product-review-comments-gap);
    }

    &__info {
      max-width: 340px;
    }
  }

  @include media.query(xl) {
    .product-reviews {
      gap: 2rem;

      &__reviews {
        flex-direction: column;
        gap: 2rem;
      }

      &__reviews-header {
        flex-basis: unset;
        width: 100%;
      }
    }
  }

  @include media.query(lg) {
    .product-reviews {
      padding: 0 0.625rem;

      &__info {
        max-width: unset;
      }
    }
  }

  @include media.query(md) {
    .product-reviews {

      &__info {
        font-size: 0.875rem;
      }

      &__opinions-wrapper {
        gap: 0.5rem;
      }

      &__counter-item-star {
        gap: 0.25rem;
        min-width: 1.75rem;
      }

      &__reviews {
        gap: 1rem;
      }

      &__reviews-header {
        gap: 1rem;
      }

      &__counter-item {
        gap: 0.375rem;
      }

      &__rate {
        gap: 0.5rem;
      }
    }
  }
</style>
